import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { X, Rocket, Users, Store, Megaphone, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

const BOOK_A_CALL_LINK = "https://calendly.com/dylantorpey/reveshare-introduction-call";
const SHOPIFY_APP_LINK = "https://apps.shopify.com/reveshare";

interface DevStoreUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const statCards = [
  { label: "Affiliates", value: "10K+", icon: Users },
  { label: "Brands", value: "100+", icon: Store },
  { label: "Campaigns", value: "150+", icon: Megaphone },
];

export function DevStoreUpgradeModal({
  isOpen,
  onClose,
}: DevStoreUpgradeModalProps) {
  const handleBookCall = () => {
    window.open(BOOK_A_CALL_LINK, "_blank");
  };

  const handleShopifyApp = () => {
    window.open(SHOPIFY_APP_LINK, "_blank");
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-card shadow-2xl transition-all">
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 z-10 rounded-full p-1.5 text-white/90 hover:bg-card/20 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Purple gradient hero banner */}
                <div className="relative overflow-hidden bg-gradient-to-br from-brand-500 via-brand-600 to-indigo-700 px-6 pt-6 pb-8">
                  {/* Subtle animated dots */}
                  <div className="absolute inset-0 overflow-hidden">
                    {[...Array(12)].map((_, i) => (
                      <span
                        key={i}
                        className="absolute w-1.5 h-1.5 rounded-full bg-card/30 animate-pulse"
                        style={{
                          left: `${10 + (i * 7) % 80}%`,
                          top: `${15 + (i * 11) % 70}%`,
                          animationDelay: `${i * 0.15}s`,
                        }}
                      />
                    ))}
                  </div>
                  <div className="relative">
                    <span className="inline-flex items-center gap-1 rounded-full bg-card/20 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Sparkles className="h-3.5 w-3.5" />
                      DEV STORE
                    </span>
                    <h2 className="mt-4 font-bold text-2xl text-white tracking-tight">
                      Reveshare
                    </h2>
                    <p className="mt-1 text-sm text-white/85">
                      Join 100+ stores already growing with Reveshare
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 pb-6 pt-2">
                  {/* Mini app preview row */}
                  <div className="flex gap-3 -mt-6 mb-5">
                    {statCards.map(({ label, value, icon: Icon }) => (
                      <div
                        key={label}
                        className="flex-1 flex flex-col items-center gap-1 rounded-xl border border-white/80 bg-card/90 py-3 px-2 shadow-lg backdrop-blur-sm"
                      >
                        <Icon className="h-5 w-5 text-brand-600" />
                        <span className="text-lg font-bold text-foreground">
                          {value}
                        </span>
                        <span className="text-xs font-medium text-muted-foreground">
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Headline + body */}
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-bold text-foreground mb-2 flex items-center gap-2"
                  >
                    <Rocket className="h-5 w-5 text-brand-600" />
                    Ready to take Reveshare further?
                  </Dialog.Title>
                  <p className="text-muted-foreground text-sm mb-6">
                    Enjoying your dev store? Book a call to go live, or install
                    Reveshare from the Shopify App Store and start scaling with
                    real affiliates.
                  </p>

                  {/* CTA buttons */}
                  <div className="flex flex-col gap-3">
                    <Button onClick={handleBookCall} className="w-full">
                      Book a Call
                    </Button>
                    <button
                      onClick={handleShopifyApp}
                      className="w-full border-2 border-brand-500 text-brand-700 font-semibold py-3 px-6 rounded-xl hover:bg-brand-50 transition-colors"
                    >
                      View on Shopify App Store
                    </button>
                  </div>

                  <button
                    onClick={onClose}
                    className="mt-3 w-full text-sm text-muted-foreground hover:text-muted-foreground transition-colors"
                  >
                    Remind me later
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
